import { Alert, Button, Form, Input, Modal, Select, Space, Typography } from "antd"
import React from "react"
import { useTranslation } from "react-i18next"

import { REASON_IDS } from "../../../../AccountDeletionRequestsPage/types/types.ts"
import { type RequestAccountDeletionParams } from "../../../api/types.ts"

const { Paragraph } = Typography

interface DeleteAccountModalProps {
  open: boolean
  loading: boolean
  onCancel: () => void
  onSubmit: (params: RequestAccountDeletionParams) => void
}

export function DeleteAccountModal({
  open,
  loading,
  onCancel,
  onSubmit
}: DeleteAccountModalProps) {
  const { t } = useTranslation()
  const [form] = Form.useForm<RequestAccountDeletionParams>()
  const reason = Form.useWatch("reason_id", form)

  return (
    <Modal
      open={open}
      title={t("accountDeletion.title")}
      footer={null}
      onCancel={onCancel}
      destroyOnClose
      centered
    >
      <Paragraph type="secondary">{t("accountDeletion.dataInfo")}</Paragraph>
      <Paragraph type="secondary">{t("accountDeletion.approver")}</Paragraph>
      <Paragraph type="secondary">{t("accountDeletion.sla")}</Paragraph>
      <Alert
        type="warning"
        showIcon
        message={t("accountDeletion.irreversible")}
        className="mb-4"
      />

      <Form
        form={form}
        layout="vertical"
        disabled={loading}
        onFinish={onSubmit}
        preserve={false}
      >
        <Form.Item
          name="reason_id"
          label={t("accountDeletion.reasonLabel")}
          rules={[{ required: true, message: t("accountDeletion.reasonRequired") }]}
        >
          <Select
            placeholder={t("accountDeletion.reasonPlaceholder")}
            options={REASON_IDS.map((id) => ({
              value: id,
              label: t(`accountDeletionRequests.reasons.${id}`)
            }))}
          />
        </Form.Item>

        {/* The backend rejects `other` without a non-blank reason_text (400). */}
        {reason === "other" && (
          <Form.Item
            name="reason_text"
            label={t("accountDeletion.reasonTextLabel")}
            rules={[
              {
                required: true,
                whitespace: true,
                message: t("accountDeletion.reasonTextRequired")
              }
            ]}
          >
            <Input.TextArea rows={3} />
          </Form.Item>
        )}

        <Space className="w-full justify-end">
          <Button onClick={onCancel}>{t("accountDeletion.cancel")}</Button>
          <Button type="primary" danger htmlType="submit" loading={loading}>
            {t("accountDeletion.submit")}
          </Button>
        </Space>
      </Form>
    </Modal>
  )
}
